import React, { useState } from 'react';
import { Gift, Mail, User, Send, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';

const DENOMINATIONS_EUR = [25, 50, 100, 150, 250];
const DENOMINATIONS_INR = [999, 1999, 4999, 9999, 14999];

const GiftVouchers = () => {
  const { currency } = useLocale();
  const denominations = currency === 'EUR' ? DENOMINATIONS_EUR : DENOMINATIONS_INR;
  const symbol = currency === 'EUR' ? '€' : '₹';
  const [amount, setAmount] = useState(denominations[1]);
  const [custom, setCustom] = useState(false);
  const [form, setForm] = useState({
    from_name: '',
    to_name: '',
    to_email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const pickAmount = (v) => { setAmount(v); setCustom(false); };

  const submit = (e) => {
    e.preventDefault();
    if (!form.to_email || !form.to_name || !amount || amount <= 0) {
      toast({ title: 'Please fill the recipient name, email and amount.' });
      return;
    }
    setSubmitting(true);
    // No mail backend yet — open the shopper's mail client with the request
    const subject = encodeURIComponent(`Feron Gift Voucher · ${symbol}${amount}`);
    const body = encodeURIComponent(
      `Hi Feron team,\n\nI'd like to purchase a gift voucher.\n\n` +
      `Amount: ${symbol}${amount}\n` +
      `From: ${form.from_name || '(not provided)'}\n` +
      `To: ${form.to_name}\n` +
      `Recipient email: ${form.to_email}\n\n` +
      `Message to include:\n${form.message || '(none)'}\n\n` +
      `Please send me a payment link and I'll settle it right away. Thanks!`
    );
    window.location.href = `mailto:feronstore@web.de?subject=${subject}&body=${body}`;
    setTimeout(() => setSubmitting(false), 800);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-14" data-testid="gift-vouchers-page">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#22c55e] via-[#16a34a] to-black text-white p-8 md:p-14 mb-10">
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-[11px] tracking-[0.25em] font-black mb-4">
              <Gift size={12} /> GIFT VOUCHERS
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight">Give the gift of streetwear.</h1>
            <p className="mt-4 text-white/90 text-base md:text-lg leading-relaxed">
              Feron vouchers land straight in your recipient's inbox with a personal note. They can spend them on anything — new drops, brand collabs, essentials. Never expires.
            </p>
          </div>
          <Gift className="absolute -right-8 -bottom-8 text-white/10" size={280} />
        </section>

        {/* Purchase card */}
        <section className="grid md:grid-cols-[1.15fr_1fr] gap-8 items-start">
          <form onSubmit={submit} className="bg-white border rounded-2xl p-6 md:p-8 space-y-5" data-testid="voucher-form">
            {/* Amount */}
            <div>
              <label className="text-xs font-bold text-gray-700 tracking-wider">CHOOSE AMOUNT</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {denominations.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => pickAmount(v)}
                    className={`py-3 rounded-lg border-2 font-black transition ${!custom && amount === v ? 'border-[#22c55e] bg-[#22c55e]/10 text-black' : 'border-gray-200 hover:border-gray-400 text-gray-600'}`}
                    data-testid={`voucher-amount-${v}`}
                  >
                    {symbol}{v}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCustom(true)}
                  className={`py-3 rounded-lg border-2 font-black transition ${custom ? 'border-[#22c55e] bg-[#22c55e]/10 text-black' : 'border-gray-200 hover:border-gray-400 text-gray-600'}`}
                  data-testid="voucher-amount-custom"
                >
                  Custom
                </button>
              </div>
              {custom && (
                <div className="mt-3">
                  <label className="text-[11px] text-gray-500">Custom amount ({symbol})</label>
                  <Input type="number" min={5} value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="voucher-custom-amount" />
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-700">YOUR NAME</label>
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="e.g. Priya" data-testid="voucher-from-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-700">RECIPIENT NAME *</label>
                <Input value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} placeholder="e.g. Alex" data-testid="voucher-to-name" required />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700">RECIPIENT EMAIL *</label>
              <Input type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} placeholder="alex@example.com" data-testid="voucher-to-email" required />
            </div>

            <div>
              <label className="text-xs font-bold text-gray-700">PERSONAL MESSAGE</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Happy birthday! Pick something amazing on Feron."
                className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-[#22c55e]"
                data-testid="voucher-message"
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-black py-6 tracking-wider" data-testid="voucher-submit">
              <Send size={16} className="mr-2" />
              {submitting ? '...' : `REQUEST ${symbol}${amount || 0} VOUCHER`}
            </Button>
            <p className="text-[11px] text-gray-500">
              Clicking send opens your mail app with the request pre-filled. We'll reply with a secure payment link and deliver the voucher to your recipient once paid.
            </p>
          </form>

          {/* Perks */}
          <aside className="space-y-4">
            {[
              { title: 'Never expires', body: 'Vouchers stay redeemable forever — no fine print.' },
              { title: 'Delivered by email', body: 'Recipient gets a beautiful branded email with a code they can apply at checkout.' },
              { title: 'Redeem on anything', body: 'Every Feron drop is eligible — from streetwear staples to limited brand collabs.' },
              { title: 'Combine with sales', body: 'Vouchers stack with any live discount code — the perfect gift, twice.' },
            ].map((p, i) => (
              <div key={p.title} className="flex gap-3 items-start p-4 bg-neutral-50 rounded-xl border" data-testid={`voucher-perk-${i}`}>
                <div className="w-8 h-8 rounded-full bg-[#22c55e] text-black flex items-center justify-center shrink-0">
                  <Check size={16} />
                </div>
                <div>
                  <p className="font-black text-sm">{p.title}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GiftVouchers;
